<template>
  <div id="admin-transcoding-outputs" class="p-4">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-bold">Transcoding Outputs</h2>
      <el-button type="primary" size="small" @click="openDialog()">
        <ma-plus class="bly-plus"></ma-plus> Add Output
      </el-button>
    </div>

    <el-table :data="outputs" style="width: 100%" v-loading="loading" class="form-dark">
      <el-table-column prop="name" label="Name" sortable></el-table-column>
      <el-table-column prop="type" label="Type" width="150" sortable>
        <template slot-scope="scope">
          <el-tag size="mini">{{ scope.row.type }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="Description"></el-table-column>
      <el-table-column label="Actions" width="150">
        <template slot-scope="scope">
          <el-button type="text" size="mini" @click="openDialog(scope.row)">Edit</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="isEdit ? 'Edit Transcoding Output' : 'Add Transcoding Output'"
      :visible.sync="dialogVisible"
      width="50%"
      class="form-dark"
    >
      <el-form :model="form" ref="form" label-width="120px" size="small">
        <el-form-item label="Name" required>
          <el-input v-model="form.name"></el-input>
        </el-form-item>
        <el-form-item label="Type" required>
          <el-select v-model="form.type" placeholder="Select type" class="w-full">
            <el-option label="MediaLive" value="MEDIALIVE"></el-option>
            <el-option label="MediaConvert" value="MEDIACONVERT"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Description">
          <el-input type="textarea" v-model="form.description"></el-input>
        </el-form-item>
        <el-form-item label="Config">
          <TranscodingOutputConfigEditor v-if="form.type === 'MEDIALIVE'" v-model="form.config" />
          <el-input v-else type="textarea" v-model="form.config" :rows="10" placeholder='{"key": "value"}'></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="small">Cancel</el-button>
        <el-button type="primary" @click="save()" :loading="saving" size="small">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import TranscodingOutputConfigEditor from "../components/Admin/TranscodingOutputConfigEditor.vue";

const { proxy } = getCurrentInstance();
const client = generateClient();

const loading = ref(false);
const saving = ref(false);
const dialogVisible = ref(false);
const isEdit = ref(false);

const outputs = computed(() => proxy.$store.state.schedule.transcodingOutputs.items || []);

const form = ref({
  id: '',
  name: '',
  type: 'MEDIALIVE',
  description: '',
  config: ''
});

const fetchOutputs = async () => {
  loading.value = true;
  try {
    await proxy.$store.dispatch('schedule/fetchTranscodingOutputs');
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const openDialog = (row = null) => {
  if (row) {
    isEdit.value = true;
    form.value = {
      id: row.id,
      name: row.name,
      type: row.type,
      description: row.description || '',
      config: typeof row.config === 'string' ? row.config : JSON.stringify(row.config, null, 2)
    };
  } else {
    isEdit.value = false;
    form.value = {
      id: '',
      name: '',
      type: 'MEDIALIVE',
      description: '',
      config: ''
    };
  }
  dialogVisible.value = true;
};

const save = async () => {
  saving.value = true;
  try {
    const variables = {
      name: form.value.name,
      type: form.value.type,
      description: form.value.description,
      config: form.value.config
    };

    let response;
    if (isEdit.value) {
      variables.id = form.value.id;
      const result = await client.graphql({
        query: mutations.patchTranscodingOutput,
        variables
      });
      response = result?.data?.patchTranscodingOutput;
    } else {
      const result = await client.graphql({
        query: mutations.addTranscodingOutput,
        variables
      });
      response = result?.data?.addTranscodingOutput;
    }

    if (response?.success === false) {
      throw new Error(response.message || 'Failed to save output');
    }

    proxy.$message.success('Output saved successfully');
    dialogVisible.value = false;
    fetchOutputs();
  } catch (e) {
    console.error(e);
    proxy.$message.error('Failed to save output');
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchOutputs();
});
</script>
